import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControl, InputLabel, Select, MenuItem,
  Alert, Chip, Tabs, Tab, CircularProgress, Avatar, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Edit as EditIcon, Block as BlockIcon, CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon, Add as AddIcon, Article as ArticleIcon, Chat as ChatBubbleIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';
import { adminAPI, isSiteOwnerEmail, type AdminUser } from '../services/api';
import { newsService, type NewsItem } from '../services/newsService';
import { blogService } from '../services/blogService';

const TAB_KEYS = ['users', 'news', 'my', 'blog'] as const;
type TabKey = typeof TAB_KEYS[number];

const AdminPage: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const articleRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const isSiteAdmin = user?.role === 'site_admin';
  const isEditor = user?.role === 'editor';
  // Владелец сайта (AleksandrP85) — те же права, что и у администратора сайта
  const isSiteOwner = isSiteOwnerEmail(user?.email);
  const isFullAdmin = isSiteAdmin || isSiteOwner;

  const availableTabs: { key: TabKey; label: string }[] = [];
  if (isFullAdmin) availableTabs.push({ key: 'users', label: 'Пользователи' });
  if (isFullAdmin || isEditor) availableTabs.push({ key: 'news', label: 'Новости' });
  availableTabs.push({ key: 'my', label: 'Мои статьи' });
  if (isFullAdmin) availableTabs.push({ key: 'blog', label: 'Блог' });

  const [tabKey, setTabKey] = useState<TabKey>(isFullAdmin ? 'users' : isEditor ? 'news' : 'my');
  const tabIndex = Math.max(0, availableTabs.findIndex(t => t.key === tabKey));

  // Handle incoming state from BlogCreatePage
  useEffect(() => {
    const state = location.state as any;
    if (state?.tab === 'blog' || state?.tab === 'my') {
      setTabKey('my');
      if (state.scrollToArticle) {
        setTimeout(() => {
          const el = articleRefs.current[state.scrollToArticle];
          if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.style.outline = '2px solid #1565c0'; setTimeout(() => { el.style.outline = ''; }, 2000); }
        }, 300);
      }
    }
  }, [location.state]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [roleDialog, setRoleDialog] = useState<{ open: boolean; userId: number; currentRole: string }>({
    open: false, userId: 0, currentRole: 'user'
  });

  const [banDialog, setBanDialog] = useState<{
    open: boolean; userId: number; userName: string;
    reason: string; banType: 'temporary' | 'permanent'; durationHours: number;
  }>({ open: false, userId: 0, userName: '', reason: '', banType: 'temporary', durationHours: 24 });

  const [news, setNews] = useState<NewsItem[]>([]);
  const [newsDialog, setNewsDialog] = useState<{ open: boolean; item: NewsItem | null; isNew: boolean }>({ open: false, item: null, isNew: false });
  const [newsForm, setNewsForm] = useState({ title: '', excerpt: '', content: '', cover_color: '#1565c0', status: 'draft' });

  // Blog admin state
  const [blogBans, setBlogBans] = useState<any[]>([]);
  const [blogSettings, setBlogSettings] = useState<any[]>([]);
  const [blogArticles, setBlogArticles] = useState<any[]>([]);
  const [articlesLoaded, setArticlesLoaded] = useState(false);
  const [blogBanDialog, setBlogBanDialog] = useState<{ open: boolean; userName?: string; userCode?: string }>({ open: false });
  const [blogBanForm, setBlogBanForm] = useState({ user_id: 0, reason: '', reason_visible: false, ban_type: 'temporary', duration_hours: 24, ban_scope: 'both' as string });

  const loadBlogBans = async () => { try { setBlogBans(await blogService.listBans()); } catch {} };
  const loadBlogSettings = async () => { try { setBlogSettings(await blogService.listSettings()); } catch {} };
  const loadBlogArticles = async () => {
    try { setBlogArticles(await blogService.listMyArticles()); }
    catch {}
    finally { setArticlesLoaded(true); }
  };

  const createBlogBan = async () => {
    try {
      await blogService.createBan(blogBanForm);
      setBlogBanDialog({ open: false });
      loadBlogBans();
    } catch (e: any) { setError(e.response?.data?.detail || 'Ошибка'); }
  };

  const deleteBlogBan = async (id: number) => {
    if (!window.confirm('Снять блокировку?')) return;
    try { await blogService.deleteBan(id); loadBlogBans(); } catch {}
  };

  const handleUnblogBan = async (userId: number, scope: string) => {
    if (!window.confirm(`Снять запрет (${scope === 'articles' ? 'статьи' : 'комментарии'})?`)) return;
    try { await blogService.deleteBanByUserScope(userId, scope); loadUsers(); } catch {}
  };

  const hasBlogBan = (u: any, scope: string) => {
    return u.blog_bans?.some((b: any) => b.scope === scope || b.scope === 'both');
  };

  const updateBlogSetting = async (key: string, value: string) => {
    try { await blogService.updateSetting(key, value); loadBlogSettings(); } catch {}
  };

  const loadNews = async () => {
    try { setNews(await newsService.getAll()); }
    catch (e: any) { console.error('loadNews error:', e); setError(e.response?.data?.detail || 'Ошибка загрузки новостей'); }
  };

  const loadUsers = async () => {
    try { setLoading(true); setUsers((await adminAPI.getUsers()).users); }
    catch (err: any) { setError(err.response?.data?.detail || 'Ошибка загрузки пользователей'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (isFullAdmin) { loadUsers(); loadBlogBans(); loadBlogSettings(); }
    if (isFullAdmin || isEditor) loadNews();
    loadBlogArticles();
  }, []);

  const openNewNews = () => {
    setNewsForm({ title: '', excerpt: '', content: '', cover_color: '#1565c0', status: 'draft' });
    setNewsDialog({ open: true, item: null, isNew: true });
  };

  const openEditNews = (item: NewsItem) => {
    setNewsForm({ title: item.title, excerpt: item.excerpt || '', content: item.content, cover_color: item.cover_color, status: item.status });
    setNewsDialog({ open: true, item, isNew: false });
  };

  const saveNews = async () => {
    if (!newsForm.title.trim()) return;
    try {
      if (newsDialog.isNew) await newsService.create(newsForm);
      else if (newsDialog.item) await newsService.update(newsDialog.item.id, newsForm);
      setNewsDialog({ open: false, item: null, isNew: false });
      loadNews();
    } catch (e: any) { console.error('saveNews error:', e); setError(e.response?.data?.detail || 'Ошибка сохранения новости'); }
  };

  const publishNews = async (id: number) => {
    try { await newsService.publish(id); await loadNews(); }
    catch (e: any) { setError(e.response?.data?.detail || 'Ошибка публикации'); }
  };

  const deleteNews = async (id: number) => {
    if (!window.confirm('Удалить новость?')) return;
    try { await newsService.delete(id); await loadNews(); }
    catch (e: any) { setError(e.response?.data?.detail || 'Ошибка удаления'); }
  };

  const handleRoleChange = async () => {
    try { await adminAPI.updateRole(roleDialog.userId, roleDialog.currentRole); setRoleDialog({ ...roleDialog, open: false }); loadUsers(); }
    catch (err: any) { setError(err.response?.data?.detail || 'Ошибка изменения роли'); }
  };

  const handleBan = async () => {
    try { await adminAPI.banUser(banDialog.userId, banDialog.reason, banDialog.banType, banDialog.durationHours); setBanDialog({ ...banDialog, open: false }); loadUsers(); }
    catch (err: any) { setError(err.response?.data?.detail || 'Ошибка блокировки'); }
  };

  const handleUnban = async (userId: number) => {
    if (!window.confirm('Разблокировать пользователя?')) return;
    try { await adminAPI.unbanUser(userId); loadUsers(); }
    catch (err: any) { setError(err.response?.data?.detail || 'Ошибка разблокировки'); }
  };

  const getRoleColor = (role: string) => {
    switch (role) { case 'site_admin': return 'error'; case 'admin': return 'warning'; case 'editor': return 'info'; default: return 'default'; }
  };

  const getRoleLabel = (role: string) => {
    switch (role) { case 'site_admin': return 'Адм. сайта'; case 'admin': return 'Адм. мероприятия'; case 'editor': return 'Редактор'; default: return 'Пользователь'; }
  };

  // Доступ: администраторы сайта, владелец сайта, редакторы и авторы статей/черновиков
  if (!isFullAdmin && !isEditor && !articlesLoaded) {
    return <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;
  }
  if (!isFullAdmin && !isEditor && blogArticles.length === 0) {
    return <Box sx={{ p: 3 }}><Alert severity="error">Доступ запрещен.</Alert></Box>;
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={800} gutterBottom sx={{ fontSize: { xs: '1.2rem', sm: '1.8rem' } }}>
        {isFullAdmin ? 'Админ-панель' : isEditor ? 'Панель редактора' : 'Мои статьи'}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper>
        <Tabs value={tabIndex} onChange={(_, v) => setTabKey(availableTabs[v].key)} variant={isMobile ? 'fullWidth' : 'standard'}>
          {availableTabs.map(t => (
            <Tab key={t.key} label={t.label} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, minHeight: { xs: 40, sm: 48 } }} />
          ))}
        </Tabs>

        {/* Users tab */}
        {tabKey === 'users' && isFullAdmin && (
          <Box sx={{ p: { xs: 1, sm: 2 } }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : isMobile ? (
              // Mobile: card layout
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {users.map(u => (
                  <Paper key={u.id} variant="outlined" sx={{ p: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem' }}>{(u.first_name || u.email || '?')[0]}</Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                            {u.first_name || u.username || '—'} {u.is_protected && '🔒'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>{u.email || '—'}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip label={getRoleLabel(u.role)} color={getRoleColor(u.role) as any} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                        {u.is_banned && <Chip label="Бан аккаунта" color="error" size="small" sx={{ height: 20, fontSize: '0.65rem' }} />}
                        {u.blog_bans?.some(b => b.scope === 'articles' || b.scope === 'both') && (
                          <Chip label="Запрет статей" size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#fff3e0', color: '#ef6c00' }} />
                        )}
                        {u.blog_bans?.some(b => b.scope === 'comments' || b.scope === 'both') && (
                          <Chip label="Запрет комм." size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#f3e5f5', color: '#9c27b0' }} />
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      <IconButton size="small" onClick={() => setRoleDialog({ open: true, userId: u.id, currentRole: u.role })} disabled={u.is_protected}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
                      {u.is_banned ? (
                        <IconButton size="small" color="success" onClick={() => handleUnban(u.id)}><CheckCircleIcon sx={{ fontSize: 16 }} /></IconButton>
                      ) : (
                        <IconButton size="small" color="error" onClick={() => setBanDialog({ open: true, userId: u.id, userName: u.first_name || u.email || '', reason: '', banType: 'temporary', durationHours: 24 })} disabled={u.is_protected || u.id === user?.id}><BlockIcon sx={{ fontSize: 16 }} /></IconButton>
                      )}
                      {hasBlogBan(u, 'articles') ? (
                        <IconButton size="small" title="Снять запрет статей" onClick={() => handleUnblogBan(u.id, 'articles')}
                          sx={{ color: '#2e7d32' }}><ArticleIcon sx={{ fontSize: 16 }} /></IconButton>
                      ) : (
                        <IconButton size="small" title="Запрет статьи" onClick={() => { setBlogBanForm({ user_id: u.id, reason: '', reason_visible: false, ban_type: 'temporary', duration_hours: 24, ban_scope: 'articles' }); setBlogBanDialog({ open: true, userName: u.first_name || u.email || `#${u.id}`, userCode: u.user_code || String(u.id) }); }}
                          sx={{ color: '#ef6c00' }} disabled={u.is_protected}><ArticleIcon sx={{ fontSize: 16 }} /></IconButton>
                      )}
                      {hasBlogBan(u, 'comments') ? (
                        <IconButton size="small" title="Снять запрет комментариев" onClick={() => handleUnblogBan(u.id, 'comments')}
                          sx={{ color: '#2e7d32' }}><ChatBubbleIcon sx={{ fontSize: 16 }} /></IconButton>
                      ) : (
                        <IconButton size="small" title="Запрет комментариев" onClick={() => { setBlogBanForm({ user_id: u.id, reason: '', reason_visible: false, ban_type: 'temporary', duration_hours: 24, ban_scope: 'comments' }); setBlogBanDialog({ open: true, userName: u.first_name || u.email || `#${u.id}`, userCode: u.user_code || String(u.id) }); }}
                          sx={{ color: '#9c27b0' }} disabled={u.is_protected}><ChatBubbleIcon sx={{ fontSize: 16 }} /></IconButton>
                      )}
                    </Box>
                  </Paper>
                ))}
              </Box>
            ) : (
              // Desktop: table layout
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell><TableCell>Email</TableCell><TableCell>Имя</TableCell>
                      <TableCell>Роль</TableCell><TableCell>Статус</TableCell><TableCell align="right">Действия</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map(u => (
                      <TableRow key={u.id} hover>
                        <TableCell>{u.id}</TableCell>
                        <TableCell>{u.email || '—'}</TableCell>
                        <TableCell>{u.first_name || u.username || '—'} {u.is_protected && '🔒'}</TableCell>
                        <TableCell><Chip label={getRoleLabel(u.role)} color={getRoleColor(u.role) as any} size="small" /></TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {u.is_banned && <Chip label="Бан" color="error" size="small" sx={{ height: 20, fontSize: '0.65rem' }} />}
                            {u.blog_bans?.some(b => b.scope === 'articles' || b.scope === 'both') && (
                              <Chip label="Статьи" size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#fff3e0', color: '#ef6c00' }} />
                            )}
                            {u.blog_bans?.some(b => b.scope === 'comments' || b.scope === 'both') && (
                              <Chip label="Комм." size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#f3e5f5', color: '#9c27b0' }} />
                            )}
                            {!u.is_banned && !u.blog_bans?.length && <Chip label="Активен" color="success" size="small" />}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => setRoleDialog({ open: true, userId: u.id, currentRole: u.role })} disabled={u.is_protected}><EditIcon fontSize="small" /></IconButton>
                          {u.is_banned ? (
                            <IconButton size="small" color="success" onClick={() => handleUnban(u.id)}><CheckCircleIcon fontSize="small" /></IconButton>
                          ) : (
                            <IconButton size="small" color="error" onClick={() => setBanDialog({ open: true, userId: u.id, userName: u.first_name || u.email || '', reason: '', banType: 'temporary', durationHours: 24 })} disabled={u.is_protected || u.id === user?.id}><BlockIcon fontSize="small" /></IconButton>
                          )}
                          {hasBlogBan(u, 'articles') ? (
                            <IconButton size="small" title="Снять запрет статей" onClick={() => handleUnblogBan(u.id, 'articles')}
                              sx={{ color: '#2e7d32' }}><ArticleIcon sx={{ fontSize: 16 }} /></IconButton>
                          ) : (
                            <IconButton size="small" title="Запрет статьи" onClick={() => { setBlogBanForm({ user_id: u.id, reason: '', reason_visible: false, ban_type: 'temporary', duration_hours: 24, ban_scope: 'articles' }); setBlogBanDialog({ open: true, userName: u.first_name || u.email || `#${u.id}`, userCode: u.user_code || String(u.id) }); }}
                              sx={{ color: '#ef6c00' }} disabled={u.is_protected}><ArticleIcon sx={{ fontSize: 16 }} /></IconButton>
                          )}
                          {hasBlogBan(u, 'comments') ? (
                            <IconButton size="small" title="Снять запрет комментариев" onClick={() => handleUnblogBan(u.id, 'comments')}
                              sx={{ color: '#2e7d32' }}><ChatBubbleIcon sx={{ fontSize: 16 }} /></IconButton>
                          ) : (
                            <IconButton size="small" title="Запрет комментариев" onClick={() => { setBlogBanForm({ user_id: u.id, reason: '', reason_visible: false, ban_type: 'temporary', duration_hours: 24, ban_scope: 'comments' }); setBlogBanDialog({ open: true, userName: u.first_name || u.email || `#${u.id}`, userCode: u.user_code || String(u.id) }); }}
                              sx={{ color: '#9c27b0' }} disabled={u.is_protected}><ChatBubbleIcon sx={{ fontSize: 16 }} /></IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* News tab */}
        {tabKey === 'news' && (
          <Box sx={{ p: { xs: 1, sm: 2 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>Новости</Typography>
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openNewNews}
                sx={{ bgcolor: '#ef6c00', '&:hover': { bgcolor: '#e65100' }, fontSize: { xs: '0.72rem', sm: '0.8rem' } }}>
                {isMobile ? 'Добавить' : 'Новая новость'}
              </Button>
            </Box>

            {news.length === 0 ? (
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>Новостей нет</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {news.map(item => (
                  <Paper key={item.id} variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700} noWrap sx={{ fontSize: { xs: '0.82rem', sm: '0.9rem' } }}>{item.title}</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                          <Chip label={item.status === 'published' ? 'Опубликовано' : 'Черновик'} size="small"
                            color={item.status === 'published' ? 'success' : 'default'} sx={{ height: 20, fontSize: '0.65rem' }} />
                          <Typography variant="caption" color="text.secondary">
                            {item.created_at ? new Date(item.created_at).toLocaleDateString('ru-RU') : '—'}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                        <IconButton size="small" onClick={() => openEditNews(item)}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
                        {item.status === 'draft' && <IconButton size="small" color="success" onClick={() => publishNews(item.id)}><CheckCircleIcon sx={{ fontSize: 16 }} /></IconButton>}
                        {isFullAdmin && <IconButton size="small" color="error" onClick={() => deleteNews(item.id)}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>}
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* My articles tab — для всех авторов статей и черновиков */}
        {tabKey === 'my' && (
          <Box sx={{ p: { xs: 1, sm: 2 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>Мои статьи и черновики</Typography>
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => navigate('/blog/create', { state: { from: 'admin' } })}
                sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' }, fontSize: '0.75rem' }}>
                {isMobile ? 'Создать' : 'Новая статья'}
              </Button>
            </Box>

            {blogArticles.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center', fontSize: '0.85rem' }}>У вас пока нет статей</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                {blogArticles.map(article => (
                  <Paper key={article.id} variant="outlined" sx={{ p: 1.5, transition: 'outline 0.3s' }}
                    ref={el => { articleRefs.current[article.id] = el; }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700} noWrap sx={{ fontSize: '0.82rem' }}>{article.title}</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                          <Chip label={article.status === 'published' ? 'Опубликовано' : 'Черновик'} size="small"
                            color={article.status === 'published' ? 'success' : 'default'} sx={{ height: 20, fontSize: '0.65rem' }} />
                          {article.event_type_name && (
                            <Chip size="small" label={`${article.event_type_emoji || ''} ${article.event_type_name}`} variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {article.published_at ? new Date(article.published_at).toLocaleDateString('ru-RU') : new Date(article.created_at).toLocaleDateString('ru-RU')}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                        <IconButton size="small" onClick={() => navigate(`/blog/${article.id}/edit`, { state: { from: 'admin' } })} title="Редактировать"><EditIcon sx={{ fontSize: 16 }} /></IconButton>
                        {article.status === 'draft' && (
                          <IconButton size="small" color="success" onClick={async () => {
                            try { await blogService.updateArticle(article.id, { status: 'published' }); loadBlogArticles(); } catch {}
                          }} title="Опубликовать"><CheckCircleIcon sx={{ fontSize: 16 }} /></IconButton>
                        )}
                        <IconButton size="small" color="error" onClick={async () => {
                          if (window.confirm('Удалить статью?')) { try { await blogService.deleteArticle(article.id); loadBlogArticles(); } catch {} }
                        }} title="Удалить"><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Blog management tab — admin only */}
        {tabKey === 'blog' && isFullAdmin && (
          <Box sx={{ p: { xs: 1, sm: 2 } }}>
            {/* Blog bans */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>Блокировки блога</Typography>
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => { setBlogBanForm({ user_id: 0, reason: '', reason_visible: false, ban_type: 'temporary', duration_hours: 24, ban_scope: 'both' }); setBlogBanDialog({ open: true }); }}
                sx={{ bgcolor: '#ef6c00', '&:hover': { bgcolor: '#e65100' }, fontSize: '0.75rem' }}>
                {isMobile ? 'Добавить' : 'Заблокировать'}
              </Button>
            </Box>

            {blogBans.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center', fontSize: '0.85rem' }}>Нет активных блокировок</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                {blogBans.map(ban => {
                  const scopeLabels: Record<string, string> = { articles: 'Статьи', comments: 'Комментарии', both: 'Статьи + Комментарии' };
                  return (
                  <Paper key={ban.id} variant="outlined" sx={{ p: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>{ban.user_name || ban.user_email} (#{ban.user_id})</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {scopeLabels[ban.ban_scope] || 'Все'} · {ban.ban_type === 'permanent' ? 'Постоянная' : `До ${ban.expires_at ? new Date(ban.expires_at).toLocaleDateString('ru-RU') : '?'}`} · {ban.reason}
                          {ban.reason_visible ? ' (видна)' : ' (скрыта)'}
                        </Typography>
                      </Box>
                      <IconButton size="small" color="success" onClick={() => deleteBlogBan(ban.id)} title="Снять"><CheckCircleIcon sx={{ fontSize: 16 }} /></IconButton>
                    </Box>
                  </Paper>
                  );
                })}
              </Box>
            )}

            {/* Blog settings */}
            <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' }, mb: 1.5 }}>Настройки блога</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {blogSettings.map(s => {
                const NAMES: Record<string, string> = {
                  'access_checks_enabled': 'Проверка доступа',
                  'organizer_published_events_min': 'Мин. мероприятий организатора',
                  'organizer_published_days': 'Период проверки (дней)',
                  'organizer_published_min_participants': 'Мин. участников',
                  'organizer_published_min_fill_pct': 'Мин. заполненность (%)',
                  'organizer_future_events_min': 'Мин. будущих мероприятий',
                  'user_attended_events_min': 'Мин. посещений пользователя',
                  'user_attended_days': 'Период проверки посещений (дней)',
                };
                return (
                <Paper key={s.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>{NAMES[s.setting_key] || s.setting_key}</Typography>
                      <Typography variant="caption" color="text.secondary">{s.description}</Typography>
                    </Box>
                    {s.setting_value === 'true' || s.setting_value === 'false' ? (
                      <Button size="small" variant={s.setting_value === 'true' ? 'contained' : 'outlined'}
                        onClick={() => updateBlogSetting(s.setting_key, s.setting_value === 'true' ? 'false' : 'true')}
                        sx={{ fontSize: '0.72rem', minWidth: 60 }}>
                        {s.setting_value === 'true' ? 'Вкл' : 'Выкл'}
                      </Button>
                    ) : (
                      <TextField size="small" value={s.setting_value} sx={{ width: 80 }}
                        onChange={e => updateBlogSetting(s.setting_key, e.target.value)}
                        inputProps={{ style: { fontSize: '0.82rem', textAlign: 'center' } }} />
                    )}
                  </Box>
                </Paper>
                );
              })}
            </Box>
          </Box>
        )}
      </Paper>

      {/* Role dialog */}
      <Dialog open={roleDialog.open} onClose={() => setRoleDialog({ ...roleDialog, open: false })} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Изменить роль</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }} size="small">
            <InputLabel>Роль</InputLabel>
            <Select value={roleDialog.currentRole} label="Роль" onChange={e => setRoleDialog({ ...roleDialog, currentRole: e.target.value })}>
              <MenuItem value="user">Пользователь</MenuItem>
              <MenuItem value="editor">Редактор</MenuItem>
              <MenuItem value="admin">Администратор мероприятия</MenuItem>
              {isSiteOwnerEmail(user?.email) && (
                <MenuItem value="site_admin">Администратор сайта</MenuItem>
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialog({ ...roleDialog, open: false })}>Отмена</Button>
          <Button onClick={handleRoleChange} variant="contained">Сохранить</Button>
        </DialogActions>
      </Dialog>

      {/* Ban dialog */}
      <Dialog open={banDialog.open} onClose={() => setBanDialog({ ...banDialog, open: false })} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Заблокировать: {banDialog.userName}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Причина" multiline rows={2} value={banDialog.reason}
            onChange={e => setBanDialog({ ...banDialog, reason: e.target.value })} sx={{ mt: 2 }} size="small" required />
          <FormControl fullWidth sx={{ mt: 2 }} size="small">
            <InputLabel>Тип</InputLabel>
            <Select value={banDialog.banType} label="Тип" onChange={e => setBanDialog({ ...banDialog, banType: e.target.value as any })}>
              <MenuItem value="temporary">Временная</MenuItem>
              <MenuItem value="permanent">Постоянная</MenuItem>
            </Select>
          </FormControl>
          {banDialog.banType === 'temporary' && (
            <TextField fullWidth label="Часов" type="number" value={banDialog.durationHours}
              onChange={e => setBanDialog({ ...banDialog, durationHours: parseInt(e.target.value) || 24 })} sx={{ mt: 2 }} size="small" inputProps={{ min: 1 }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBanDialog({ ...banDialog, open: false })}>Отмена</Button>
          <Button onClick={handleBan} variant="contained" color="error" disabled={!banDialog.reason.trim()}>Заблокировать</Button>
        </DialogActions>
      </Dialog>

      {/* News dialog */}
      <Dialog open={newsDialog.open} onClose={() => setNewsDialog({ ...newsDialog, open: false })} fullScreen={isMobile} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>{newsDialog.isNew ? 'Новая новость' : 'Редактирование'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField fullWidth label="Заголовок" value={newsForm.title} onChange={e => setNewsForm({ ...newsForm, title: e.target.value })} required size="small" />
          <TextField fullWidth label="Краткое описание" value={newsForm.excerpt} onChange={e => setNewsForm({ ...newsForm, excerpt: e.target.value })} size="small" multiline rows={2} />
          <TextField fullWidth label="Содержимое (Markdown)" value={newsForm.content} onChange={e => setNewsForm({ ...newsForm, content: e.target.value })} size="small" multiline rows={isMobile ? 6 : 10} />
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 140, flex: 1 }}>
              <InputLabel>Статус</InputLabel>
              <Select value={newsForm.status} label="Статус" onChange={e => setNewsForm({ ...newsForm, status: e.target.value as any })}>
                <MenuItem value="draft">Черновик</MenuItem>
                <MenuItem value="published">Опубликовано</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Цвет" type="color" value={newsForm.cover_color} onChange={e => setNewsForm({ ...newsForm, cover_color: e.target.value })} size="small" sx={{ width: 100 }} InputLabelProps={{ shrink: true }} />
          </Box>
          {newsForm.status === 'draft' && <Alert severity="info" sx={{ fontSize: '0.78rem' }}>Черновик виден только администратору и редактору.</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewsDialog({ ...newsDialog, open: false })}>Отмена</Button>
          <Button onClick={saveNews} variant="contained" disabled={!newsForm.title.trim()}>{newsDialog.isNew ? 'Создать' : 'Сохранить'}</Button>
        </DialogActions>
      </Dialog>

      {/* Blog ban dialog */}
      <Dialog open={blogBanDialog.open} onClose={() => setBlogBanDialog({ open: false })} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Блокировка блога</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField fullWidth label="Пользователь" size="small"
            value={`${blogBanDialog.userName || ''} (${blogBanDialog.userCode || ''})`}
            InputProps={{ readOnly: true }} sx={{ '& .MuiInputBase-input': { color: '#555' } }} />
          <FormControl size="small" fullWidth>
            <InputLabel>Область запрета</InputLabel>
            <Select value={blogBanForm.ban_scope} label="Область запрета"
              onChange={e => setBlogBanForm({ ...blogBanForm, ban_scope: e.target.value })}>
              <MenuItem value="articles">Статьи в блоге</MenuItem>
              <MenuItem value="comments">Комментарии к статьям</MenuItem>
              <MenuItem value="both">Статьи и комментарии</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth label="Причина блокировки" multiline rows={2} size="small"
            value={blogBanForm.reason} onChange={e => setBlogBanForm({ ...blogBanForm, reason: e.target.value })} required />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 140, flex: 1 }}>
              <InputLabel>Тип</InputLabel>
              <Select value={blogBanForm.ban_type} label="Тип" onChange={e => setBlogBanForm({ ...blogBanForm, ban_type: e.target.value })}>
                <MenuItem value="temporary">Временная</MenuItem>
                <MenuItem value="permanent">Постоянная</MenuItem>
              </Select>
            </FormControl>
            {blogBanForm.ban_type === 'temporary' && (
              <TextField label="Часов" type="number" size="small" sx={{ width: 100 }}
                value={blogBanForm.duration_hours} onChange={e => setBlogBanForm({ ...blogBanForm, duration_hours: Number(e.target.value) })}
                inputProps={{ min: 1 }} />
            )}
          </Box>
          <Box>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.85rem' }}>
              <input type="checkbox" checked={blogBanForm.reason_visible}
                onChange={e => setBlogBanForm({ ...blogBanForm, reason_visible: e.target.checked })} />
              Показывать причину пользователю
            </label>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlogBanDialog({ open: false })}>Отмена</Button>
          <Button onClick={createBlogBan} variant="contained" color="error" disabled={!blogBanForm.reason.trim() || !blogBanForm.user_id}>Заблокировать</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPage;